import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MdClose, MdArrowBack } from 'react-icons/md';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { fetchUpcomingEventsRequest } from '../../redux/slices/upcomingEventsSlice';
import { createCampaignRequest, resetCampaignCreation } from '../../redux/slices/campaignCreationSlice';
import type { RootState } from '../../redux/store';

import { createDirectCampaign } from '../../api/contentApi';
import { getRestaurantId } from '../../utils/restaurantId';
import CreateOwnForm from '../editCampaign/CreateOwnForm';
import EventsList from '../editCampaign/EventsList';
import LoadingState from '../common/LoadingState';
import ErrorState from '../common/ErrorState';
import { useRestaurantNav } from '../../hooks/useRestaurantNav';

interface CreateCampaignDrawerProps {
  isOpen: boolean;
  onClose: () => void;
}

type ViewState = 'selection' | 'create-own' | 'events-list' | 'event-form';

const CreateCampaignDrawer: React.FC<CreateCampaignDrawerProps> = ({ isOpen, onClose }) => {
  const dispatch = useDispatch();
  const { navigate } = useRestaurantNav();
  const restaurantId = "4bebbb8a-a2bf-4d53-b287-842c3c5be847";
  // const restaurantId = useSelector((state: RootState) => state.clover.restaurantId);

  const [currentView, setCurrentView] = useState<ViewState>('selection');
  const [selectedOption, setSelectedOption] = useState<string>('create-own');
  const [selectedEvent, setSelectedEvent] = useState<any>(null);
  const [isFormValid, setIsFormValid] = useState<boolean>(false);

  // Get upcoming events from Redux store
  const { combinedEvents, loading, error } = useSelector((state: RootState) => state.upcomingEvents);
  
  // Get campaign creation state (used for both create-own and event-based)
  const { loading: creationLoading, success: creationSuccess, error: creationError, response: creationResponse } = useSelector((state: RootState) => state.campaignCreation);

  // Fetch events when drawer opens and "generated-events" is selected
  useEffect(() => {
    if (isOpen && selectedOption === 'generated-events' && restaurantId) {
      const countryCode = 'US'; // Can be made dynamic based on user preference
      dispatch(fetchUpcomingEventsRequest({ restaurantId, countryCode }));
    }
  }, [isOpen, selectedOption, dispatch, restaurantId]);

  // Handle campaign creation success (both create-own and event-based campaigns)
  useEffect(() => {
    if (creationSuccess && !creationLoading && restaurantId) {
      handleClose();
      
      // Navigate to campaign generator with taskId and campaignId
      const taskId = creationResponse?.data?.taskId;
      const campaignId = creationResponse?.data?.id;
      
      if (taskId && campaignId) {
        navigate(`/${restaurantId}/campaign-generator`, {
          state: {
            taskId: taskId,
            campaignId: campaignId,
          }
        });
      } else {
        // Fallback: navigate to campaigns page if no taskId
        navigate(`/${restaurantId}/campaigns`);
      }
      
      dispatch(resetCampaignCreation());
    }
  }, [creationSuccess, creationLoading, navigate, dispatch, restaurantId, creationResponse]);

  // Handle campaign creation error
  useEffect(() => {
    if (creationError && !creationLoading) {
      console.error('Campaign creation error:', creationError);
    }
  }, [creationError, creationLoading]);

  const options = [
    { 
      id: 'create-own', 
      title: 'Create your own campaign',
      description: 'Design a custom campaign from scratch with full control over content, timing, and targeting',
      icon: (
        <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 20 20">
          <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
        </svg>
      )
    },
    { 
      id: 'generated-events', 
      title: 'Generate campaign from event',
      description: 'Use AI-powered campaign suggestions based on your business goals and audience insights',
      icon: (
        <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 20 20">
          <path d="M11 3a1 1 0 10-2 0v1a1 1 0 102 0V3zM15.657 5.757a1 1 0 00-1.414-1.414l-.707.707a1 1 0 001.414 1.414l.707-.707zM18 10a1 1 0 01-1 1h-1a1 1 0 110-2h1a1 1 0 011 1zM5.05 6.464A1 1 0 106.464 5.05l-.707-.707a1 1 0 00-1.414 1.414l.707.707zM5 10a1 1 0 01-1 1H3a1 1 0 110-2h1a1 1 0 011 1zM8 16v-1h4v1a2 2 0 11-4 0zM12 14c.015-.34.208-.646.477-.859a4 4 0 10-4.954 0c.27.213.462.519.476.859h4.002z" />
        </svg>
      )
    }
  ];

  const handleNext = () => {
    if (selectedOption === 'create-own') {
      setCurrentView('create-own');
      setSelectedEvent(null);
    } else {
      setCurrentView('events-list');
    }
  };

  const handleBack = () => {
    if (currentView === 'event-form') {
      if (selectedEvent) {
        setCurrentView('events-list');
      } else {
        setCurrentView('selection');
      }
    } else if (currentView === 'events-list') {
      setCurrentView('selection');
    } else {
      setCurrentView('selection');
    }
  };

  const handleFormSubmit = (data: any) => {
   
    if (!restaurantId) {
      console.error('No restaurant ID found!');
      return;
    }
    
    // Build campaign payload
    const campaignPayload = {
      type: 'MANUAL',
      title: data.title,
      description: data.title,
      offerDetails: data.offerDetails || '',
      instructions: data.Instructions || '',
      tagLine: data.tagLine || '',
      contactInfo: data.contactInfo || '',
      validFrom: data.validFrom,
      validTo: data.validTo,
      themeColour: data.themeColour || '#EE4423',
      menuItems: data.menuItems || [],
      refImage: data.refImage || [],
      combo: data.combo || [],
      eventId: selectedEvent?.id || '',
      event: selectedEvent || {},
    };
    
    dispatch(createCampaignRequest(campaignPayload));
  };

  const handleEventSelect = (event: any) => {
    setSelectedEvent(event);
  };

  const handleContinueFromEvents = () => {
    if (selectedEvent) {
      setCurrentView('event-form');
    }
  };

  const handleClose = () => {
    // Reset all state to default
    setCurrentView('selection');
    setSelectedOption('create-own');
    setSelectedEvent(null);
    setIsFormValid(false);
    onClose();
  };

  const getButtonText = () => {
    if (currentView === 'selection') return 'Next';
    if (currentView === 'create-own' || currentView === 'event-form') {
      return creationLoading ? 'Generating Now...' : 'Generate Campaign';
    }
    if (currentView === 'events-list') return 'Create Campaign';
    return 'Continue';
  };

  const handleButtonClick = () => {
    if (currentView === 'selection') {
      handleNext();
    } else if (currentView === 'events-list') {
      handleContinueFromEvents();
    } else if (currentView === 'create-own' || currentView === 'event-form') {
      // Trigger form submission
      const form = document.querySelector('form');
      if (form) {
        form.requestSubmit();
      }
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={handleClose}
            className="fixed inset-0 bg-black/60 z-40"
          />

          {/* XL Centered Modal */}
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ duration: 0.3 }}
              className="w-full max-w-7xl h-[85vh] flex flex-col rounded-2xl overflow-hidden"
              style={{ 
                backdropFilter: "blur(10px)",
                boxShadow: "rgba(0,0,0,0.3) 0px 0px 30px 0px",
                background: "linear-gradient(90deg,rgba(33,33,33,0.98),rgba(55,55,55,0.98))",
                border: "1px solid rgba(224,224,224,0.13)"
              }}
            >
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4">
              <div className="flex items-center gap-3">
                {currentView !== 'selection' && (
                  <button
                    onClick={handleBack}
                    className="p-2 hover:bg-gray-700/50 rounded-full transition-colors"
                  >
                    <MdArrowBack size={24} className="text-white" />
                  </button>
                )}
                {currentView === 'events-list' && (
                  <h2 className="text-xl font-medium text-white"> Select an event</h2>
                )}
                {currentView === 'event-form' && selectedEvent && (
                  <h2 className="text-xl font-bold text-white">Create Campaign</h2>
                )}
              </div>
              
              <button
                onClick={handleClose}
                className="p-2 hover:bg-gray-700/50 rounded-full transition-colors"
              >
                <MdClose size={24} className="text-white" />
              </button>
            </div>

            {/* Content Area - Scrollable */}
            <div className="flex-1 overflow-y-auto flex flex-col">
              <AnimatePresence mode="wait">
                {currentView === 'selection' && (
                  <motion.div
                    key="selection"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    className="flex-1 flex items-center justify-center p-8"
                  >
                    <div className="w-full max-w-7xl grid grid-cols-2 gap-8 items-center">
                      {/* Left Side - Campaign Image */}
                      <motion.div
                        initial={{ opacity: 0, x: -50 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.5 }}
                        className="flex items-center justify-center p-8"
                      >
                        <img 
                          src="/assets/images/drawer/campaign.png" 
                          alt="Campaign Creation"
                          className="w-full h-100 max-w-lg object-contain"
                        />
                      </motion.div>

                      {/* Right Side - Options stacked vertically */}
                      <div className="flex flex-col gap-4">
                        {options.map((option, index) => (
                          <motion.div
                            key={option.id}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.2 + (index * 0.1) }}
                            onClick={() => setSelectedOption(option.id)}
                            className={`relative cursor-pointer rounded-2xl p-6 border-2 transition-all ${
                              selectedOption === option.id 
                                ? 'border-[#EE4423] bg-[#212121]' 
                                : 'border-gray-700 bg-[#212121] hover:border-gray-600'
                            }`}
                          >
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <h3 className="text-white text-md font-medium mb-2">
                                  {option.title}
                                </h3>
                                <p className="text-gray-400 text-xs">
                                  {option.description}
                                </p>
                              </div>
                              
                              <div className="w-6 h-6 flex-shrink-0 ml-4">
                                <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-colors ${
                                  selectedOption === option.id 
                                    ? 'border-[#EE4423]' 
                                    : 'border-gray-500'
                                }`}>
                                  {selectedOption === option.id && (
                                    <motion.div
                                      initial={{ scale: 0 }}
                                      animate={{ scale: 1 }}
                                      className="w-3 h-3 rounded-full bg-[#EE4423]"
                                    />
                                  )}
                                </div>
                              </div>
                            </div>
                          </motion.div>
                        ))}
                      </div>
                    </div>
                  </motion.div>
                )}

                {currentView === 'events-list' && (
                  <>
                    {loading ? (
                      <div className="flex-1 flex items-center justify-center">
                        <LoadingState message="Loading upcoming events..." backgroundColor="bg-transparent" />
                      </div>
                    ) : error ? (
                      <div className="flex-1 flex items-center justify-center p-8">
                        <ErrorState message={error} />
                      </div>
                    ) : (
                      <EventsList key="events-list" events={combinedEvents} onSelectEvent={handleEventSelect} selectedEvent={selectedEvent} />
                    )}
                  </>
                )}

                {(currentView === 'create-own' || currentView === 'event-form') && (
                  <CreateOwnForm 
                    key={currentView}
                    onSubmit={handleFormSubmit}
                    initialData={selectedEvent ? {
                      eventDate: selectedEvent.date,
                      title: selectedEvent.name,
                      promotionType: selectedEvent.type || '',
                      offerDetails: selectedEvent.marketing_angle || '',
                      Instructions: selectedEvent.consumer_behavior || '',
                      tagLine: selectedEvent.marketing_angle || '',
                    } : undefined}
                  />
                )}
              </AnimatePresence>
            </div>

            {/* Sticky Button */}
            <div className="p-6 flex justify-end">
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={handleButtonClick}
                disabled={
                  (currentView === 'events-list' && !selectedEvent) ||
                  ((currentView === 'create-own' || currentView === 'event-form') && creationLoading)
                }
                className={`px-8 py-2 text-white text-sm font-medium rounded-xl border-1 transition-colors ${
                  ((currentView === 'events-list' && !selectedEvent) || ((currentView === 'create-own' || currentView === 'event-form') && creationLoading))
                    ? 'border-gray-600 text-gray-400 cursor-not-allowed' 
                    : 'border-[#EE4423] text-[#EE4423] hover:bg-[#EE4423] hover:text-white cursor-pointer'
                }`}
              >
                {getButtonText()}
              </motion.button>
            </div>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  );
};

export default CreateCampaignDrawer;
